import { Fragment } from "react";

interface InTextLinkProps {
  text: string;
}

type MdGroupType = "string" | "link";

export interface MdGroup {
  type: MdGroupType;
  text?: string;
  href?: string;
}

/**
 * Parses markdown-style links from text groups.
 *
 * @param groups - Array of text and link groups to process
 */
export const linkMatch = (groups: MdGroup[]): MdGroup[] => {
  const newGroups: MdGroup[] = [];
  const urlRe = /\[([^[]+)\]\((.*)\)/gm;
  let match;
  groups.forEach((group) => {
    if (group.type === "string" && group.text !== undefined) {
      let i = 0;
      while ((match = urlRe.exec(group.text)) !== null) {
        if (match.index > i) {
          newGroups.push({ type: "string", text: group.text.substring(i, match.index) });
        }
        newGroups.push({ type: "link", text: match[1], href: match[2] });
        i = match.index + match[0].length;
      }
      if (i < group.text.length) {
        newGroups.push({ type: "string", text: group.text.substring(i) });
      }
    } else newGroups.push(group);
  });
  return newGroups;
};

/**
 * Parses plain HTTP URLs from text groups.
 *
 * @param groups - Array of text and link groups to process
 */
export const baseLinkMatch = (groups: MdGroup[]): MdGroup[] => {
  const newGroups: MdGroup[] = [];
  const urlRe = /(http:\/\/[\w\d.]+)/gm;
  let match;
  groups.forEach((group) => {
    if (group.type === "string" && group.text !== undefined) {
      let i = 0;
      while ((match = urlRe.exec(group.text)) !== null) {
        /* istanbul ignore else */
        if (match.index > i) {
          newGroups.push({ type: "string", text: group.text.substring(i, match.index) });
        }
        newGroups.push({ type: "link", text: "link", href: match[1] });
        i = match.index + match[0].length;
      }
      if (i < group.text.length) {
        newGroups.push({ type: "string", text: group.text.substring(i) });
      }
    } else newGroups.push(group);
  });
  return newGroups;
};

/**
 * Component that converts markdown-style and plain URLs in text to clickable links.
 *
 * @param props - The component props
 * @param props.text - Text content that may contain markdown-style links
 */
export const InTextLink = ({ text }: InTextLinkProps): React.ReactElement => {
  let groups: MdGroup[] = [{ type: "string", text }];
  groups = linkMatch(groups);
  groups = baseLinkMatch(groups);
  return (
    <>
      {groups.map((group, i) => {
        switch (group.type) {
          case "string": {
            return <Fragment key={i}>{group.text}</Fragment>;
          }
          case "link": {
            return (
              <a
                key={i}
                href={group.href}
                target="_blank"
                rel="noreferrer"
              >
                {group.text}
              </a>
            );
          }
        }
      })}
    </>
  );
};

InTextLink.displayName = "InTextLink";
